import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, finalize, from, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models/user';
import { SupportedLanguage } from '../i18n/translations';

const USER_STORAGE_KEY = 'current_user';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  readonly currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();
  private refreshInFlight$?: Observable<{ sessionToken: string; refreshToken: string }>;

  constructor(private readonly http: HttpClient) {
    this.loadUserFromStorage();
  }

  get currentUserSnapshot(): User | null {
    return this.currentUserSubject.value;
  }

  private get storage(): Storage | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      return null;
    }
  }

  private loadUserFromStorage(): void {
    const storage = this.storage;
    const stored = storage?.getItem(USER_STORAGE_KEY);
    if (stored) {
      try {
        const raw = JSON.parse(stored) as Partial<User> & { name?: string };
        const username = raw?.username ?? raw?.name ?? '';
        if (!username) {
          storage?.removeItem(USER_STORAGE_KEY);
          return;
        }
        if (!raw.sessionToken && !raw.refreshToken) {
          storage?.removeItem(USER_STORAGE_KEY);
          return;
        }
        this.currentUserSubject.next({ ...(raw as User), username });
      } catch {
        storage?.removeItem(USER_STORAGE_KEY);
      }
    }
  }

  private saveUserToStorage(user: User): void {
    this.storage?.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  }

  private clearUserFromStorage(): void {
    this.storage?.removeItem(USER_STORAGE_KEY);
  }

  clearSession(): void {
    this.currentUserSubject.next(null);
    this.clearUserFromStorage();
  }

  refreshCurrentUser(): Observable<User | null> {
    return this.http.get<User>(`${environment.apiBaseUrl}/users/me`).pipe(
      tap(user => {
        const withToken = this.attachTokens(user);
        this.currentUserSubject.next(withToken);
        this.saveUserToStorage(withToken);
      }),
      catchError(error => {
        if (error.status === 401) {
          this.clearSession();
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  register(payload: { username: string; email: string; password: string; language: SupportedLanguage }): Observable<User> {
    return this.http.post<User>(`${environment.apiBaseUrl}/users/register`, payload).pipe(tap(user => {
      const withToken = this.attachTokens(user);
      this.currentUserSubject.next(withToken);
      this.saveUserToStorage(withToken);
    }));
  }

  checkUsernameAvailability(username: string): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${environment.apiBaseUrl}/users/username-availability`, {
      params: { username: username.trim() }
    });
  }

  login(email: string, password: string): Observable<User> {
    return this.http.post<User>(`${environment.apiBaseUrl}/users/login`, { email, password }).pipe(tap(user => {
      const withToken = this.attachTokens(user);
      this.currentUserSubject.next(withToken);
      this.saveUserToStorage(withToken);
    }));
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.http.post<void>(`${environment.apiBaseUrl}/users/password/forgot`, { email });
  }

  verifyPasswordResetCode(email: string, code: string): Observable<void> {
    return this.http.post<void>(`${environment.apiBaseUrl}/users/password/verify-code`, { email, code });
  }

  resetPasswordWithCode(email: string, code: string, password: string): Observable<User> {
    return this.http.post<User>(`${environment.apiBaseUrl}/users/password/reset`, { email, code, password }).pipe(
      tap(user => {
        const withToken = this.attachTokens(user);
        this.currentUserSubject.next(withToken);
        this.saveUserToStorage(withToken);
      })
    );
  }

  sendEmailVerificationCode(): Observable<void> {
    return this.http.post<void>(`${environment.apiBaseUrl}/users/email/verification/send`, {});
  }

  confirmEmailVerificationCode(code: string): Observable<User> {
    return this.http.post<User>(`${environment.apiBaseUrl}/users/email/verification/confirm`, { code }).pipe(
      tap(user => {
        const withToken = this.attachTokens(user);
        this.currentUserSubject.next(withToken);
        this.saveUserToStorage(withToken);
      })
    );
  }

  refreshSessionTokens(): Observable<{ sessionToken: string; refreshToken: string }> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.currentUserSubject.value?.refreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('missing_refresh_token'));
    }

    const request$ = this.http
      .post<{ sessionToken: string; refreshToken: string }>(`${environment.apiBaseUrl}/users/refresh`, { refreshToken })
      .pipe(
        tap(tokens => {
          const current = this.currentUserSubject.value;
          if (!current) {
            return;
          }
          const updated: User = { ...current, sessionToken: tokens.sessionToken, refreshToken: tokens.refreshToken };
          this.currentUserSubject.next(updated);
          this.saveUserToStorage(updated);
        }),
        finalize(() => {
          this.refreshInFlight$ = undefined;
        }),
        shareReplay(1)
      );

    this.refreshInFlight$ = request$;
    return request$;
  }

  logout(): Observable<void> {
    const refreshToken = this.currentUserSubject.value?.refreshToken;
    this.clearSession();
    return this.http
      .post<void>(`${environment.apiBaseUrl}/users/logout`, refreshToken ? { refreshToken } : {})
      .pipe(catchError(() => of(void 0)));
  }

  updateAccount(payload: {
    username: string;
    email: string;
    currentPassword?: string;
    newPassword?: string;
    language?: SupportedLanguage;
  }): Observable<User> {
    return this.http.put<User>(`${environment.apiBaseUrl}/users/me`, payload).pipe(
      tap(user => {
        const withToken = this.attachTokens(user);
        this.currentUserSubject.next(withToken);
        this.saveUserToStorage(withToken);
      })
    );
  }

  requestAccountDeletionCode(): Observable<void> {
    return this.http.post<void>(`${environment.apiBaseUrl}/users/me/deletion/request`, {});
  }

  deleteAccount(payload: { password: string; code: string }): Observable<void> {
    return this.http
      .request<void>('DELETE', `${environment.apiBaseUrl}/users/me`, {
        body: { password: payload.password.trim(), code: payload.code.trim() }
      })
      .pipe(
        tap(() => {
          this.clearSession();
        })
      );
  }

  uploadAvatar(file: File): Observable<User> {
    const contentType = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
    const fileName = file.name || 'avatar';

    return this.http
      .post<AvatarUploadUrlResponse>(`${environment.apiBaseUrl}/users/me/avatar/upload-url`, { fileName, contentType })
      .pipe(
        switchMap(upload =>
          from(
            fetch(upload.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': contentType
              }
            })
          ).pipe(
            switchMap(response => {
              if (!response.ok) {
                return throwError(() => new Error('upload_failed'));
              }

              return this.http.put<User>(
                `${environment.apiBaseUrl}/users/me/avatar`,
                { key: upload.key }
              );
            }),
            map(user => ({
              ...user,
              avatarUrl: upload.publicUrl || user.avatarUrl
            }))
          )
        ),
        tap(user => {
          const withToken = this.attachTokens(user);
          this.currentUserSubject.next(withToken);
          this.saveUserToStorage(withToken);
        })
      );
  }

  removeAvatar(): Observable<User> {
    return this.http
      .delete<User>(`${environment.apiBaseUrl}/users/me/avatar`)
      .pipe(
        tap(user => {
          const withToken = this.attachTokens(user);
          this.currentUserSubject.next(withToken);
          this.saveUserToStorage(withToken);
        })
      );
  }

  private attachTokens(user: User): User {
    const existing = this.currentUserSubject.value;
    const sessionToken = user.sessionToken ?? existing?.sessionToken;
    const refreshToken = user.refreshToken ?? existing?.refreshToken;

    return {
      ...user,
      ...(sessionToken ? { sessionToken } : {}),
      ...(refreshToken ? { refreshToken } : {})
    };
  }
}

interface AvatarUploadUrlResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
}
